/* =============================================================
   Milliard theme — global scripts
   Vanilla JS custom elements, no dependencies.
   Exposes window.MBTheme: { cart, trapFocus, releaseFocus, debounce }
   Events dispatched on document:
     cart:updated  { detail: { cart, sections } }
     cart:error    { detail: { message } }
   ============================================================= */
(() => {
  'use strict';

  const routes = window.MBTheme?.routes || {
    cart: '/cart',
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartGet: '/cart.js',
    cartUpdate: '/cart/update.js',
  };

  /* ---------- utilities ---------- */
  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  /* tabindex="-1" exclusions cover scrim/backdrop buttons: they must be clickable
     but never tab-reachable or the trap's first stop is an invisible control */
  const FOCUSABLE = 'summary:not([tabindex^="-"]), a[href]:not([tabindex^="-"]), button:not([disabled]):not([tabindex^="-"]), [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):not([disabled]), select, textarea, iframe';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Traps are kept on a stack so overlays can nest (e.g. a modal opened from
     the cart drawer): only the top trap has live listeners, and releasing it
     re-arms the one underneath. */
  const trapStack = [];
  const activateTrap = (trap) => {
    document.addEventListener('keydown', trap.handlers.keydown);
    document.addEventListener('focusin', trap.handlers.focusin);
  };
  const deactivateTrap = (trap) => {
    document.removeEventListener('keydown', trap.handlers.keydown);
    document.removeEventListener('focusin', trap.handlers.focusin);
  };

  function trapFocus(container, initial, { moveFocus = true } = {}) {
    if (!container) return;
    const elements = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === initial);
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    const handlers = {
      keydown(e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      },
      focusin(e) {
        if (!container.contains(e.target)) first.focus();
      },
    };
    const top = trapStack[trapStack.length - 1];
    if (top) {
      deactivateTrap(top);
      if (top.container === container) trapStack.pop(); // re-trap after refresh, not nest
    }
    const trap = { container, handlers };
    trapStack.push(trap);
    activateTrap(trap);
    if (moveFocus) (initial || first).focus({ preventScroll: true });
  }

  function releaseFocus() {
    const trap = trapStack.pop();
    if (!trap) return;
    deactivateTrap(trap);
    const previous = trapStack[trapStack.length - 1];
    if (previous) activateTrap(previous);
  }

  /* Body scroll lock is counted for the same reason: two open overlays must
     not unlock the page when the first one closes. */
  let scrollLocks = 0;
  const lockScroll = () => {
    scrollLocks += 1;
    document.body.classList.add('overflow-hidden');
  };
  const unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.body.classList.remove('overflow-hidden');
  };

  /* ---------- cart API ---------- */
  const SECTION_IDS = ['cart-drawer', 'cart-icon-bubble'];

  /* Cart apps (Selleasy, Fast Bundle, …) monkey-patch window.fetch to observe
     cart calls, and a crash inside their wrapper rejects OUR promise after the
     request already reached Shopify. Keep a reference captured at theme load
     for verification calls that must not depend on app wrappers. */
  const nativeFetch = window.fetch.bind(window);

  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data.description || data.message || window.MBTheme?.strings?.cartError || 'Something went wrong. Please try again.';
      const error = new Error(message);
      error.data = data;
      throw error;
    }
    return data;
  }

  /* Network-level failures reject with browser-internal text ("Failed to
     fetch") — only cartRequest errors carry .data and a user-facing message. */
  function friendlyCartError(error) {
    return error && error.data !== undefined
      ? error.message
      : window.MBTheme?.strings?.cartError || 'Something went wrong. Please try again.';
  }

  function sectionsPayload() {
    return { sections: SECTION_IDS.join(','), sections_url: window.location.pathname };
  }

  function broadcastCart(result) {
    updateSections(result.sections || {});
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: result.cart || result, sections: result.sections } }));
  }

  function updateSections(sections) {
    if (!sections) return;
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const fresh = html.getElementById('cart-icon-bubble');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      }
    }
    if (sections['cart-drawer']) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.refresh(sections['cart-drawer']);
    }
  }

  /* All cart mutations run through one queue so rapid clicks (add, +/-,
     remove) can't resolve out of order and broadcast a stale cart. */
  let cartChain = Promise.resolve();
  function enqueueCart(operation) {
    const run = cartChain.then(operation, operation);
    cartChain = run.catch(() => {});
    return run;
  }

  const cart = {
    async get() {
      const response = await fetch(routes.cartGet, { headers: { Accept: 'application/json' } });
      return response.json();
    },
    add(body) {
      return enqueueCart(async () => {
        const payload = { ...body, ...sectionsPayload() };
        let result;
        try {
          result = await cartRequest(routes.cartAdd, payload);
        } catch (error) {
          if (error && error.data !== undefined) throw error; // genuine Shopify rejection (422 etc.)
          /* Network-layer rejection — often an app's fetch wrapper crashing
             AFTER Shopify accepted the add. Verify against the cart before
             reporting a false failure (a retry would double-add). */
          const wanted = (body.items || [{ id: body.id }]).map((i) => Number(i.id));
          const verify = await nativeFetch(routes.cartGet, { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null);
          const landed = verify && verify.items && wanted.every((id) => verify.items.some((i) => i.variant_id === id));
          if (!landed) throw error;
          /* re-render the drawer/bubble from the server so the opened drawer
             shows the new item, not the stale pre-add markup (sections:null
             would leave both untouched) */
          const freshSections = await nativeFetch(`${routes.cart}?sections=${SECTION_IDS.join(',')}`, { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null);
          broadcastCart({ cart: verify, sections: freshSections });
          return verify;
        }
        // /cart/add.js returns items, not the cart — sections came inline; get cart totals fresh
        let fresh = null;
        try {
          fresh = await this.get();
        } catch {
          /* the add itself succeeded — never surface the follow-up totals
             fetch as an add failure (a retry would double-add) */
        }
        broadcastCart({ cart: fresh || result, sections: result.sections });
        return fresh || result;
      });
    },
    change(key, quantity) {
      return enqueueCart(async () => {
        const result = await cartRequest(routes.cartChange, { id: key, quantity, ...sectionsPayload() });
        broadcastCart({ cart: result, sections: result.sections });
        return result;
      });
    },
    update(body) {
      return enqueueCart(async () => {
        const result = await cartRequest(routes.cartUpdate, { ...body, ...sectionsPayload() });
        broadcastCart({ cart: result, sections: result.sections });
        return result;
      });
    },
  };

  window.MBTheme = Object.assign(window.MBTheme || {}, { cart, trapFocus, releaseFocus, debounce });

  /* ---------- <quantity-input> ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn || !this.input) return;
        e.preventDefault();
        const previous = this.input.value;
        if (btn.name === 'plus') this.input.stepUp();
        if (btn.name === 'minus') this.input.stepDown();
        if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- <product-form> (AJAX add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.status = this.querySelector('[data-form-status]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(evt) {
      if (this.dataset.ajax === 'false') return; // fall through to normal POST
      evt.preventDefault();
      if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;
      if (this.submitButton.getAttribute('aria-busy') === 'true') return; // request already in flight

      const formData = new FormData(this.form);
      const body = {
        items: [
          {
            id: Number(formData.get('id')),
            quantity: Number(formData.get('quantity') || 1),
          },
        ],
      };
      // pass line-item properties if present
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[')) {
          const name = key.slice(11, -1);
          body.items[0].properties = body.items[0].properties || {};
          body.items[0].properties[name] = value;
        }
      }

      this.setLoading(true);
      this.announce('');
      cart
        .add(body)
        .then((updatedCart) => {
          this.announce(window.MBTheme?.strings?.addedToCart || 'Added to cart.', 'success');
          const drawer = document.querySelector('cart-drawer');
          if (drawer && window.MBTheme?.settings?.cartType === 'drawer') {
            /* Safety net: an app fetch-wrapper (Selleasy/Fast Bundle, etc.) can
               strip the section HTML from the add response, leaving the drawer
               on its stale empty state. If the cart has items but the drawer
               still shows empty, re-render it from the server before opening so
               it never opens empty on a non-empty cart. */
            const count = updatedCart && (updatedCart.item_count != null ? updatedCart.item_count : updatedCart.items && updatedCart.items.length);
            if (count > 0 && drawer.querySelector('.cart-empty')) {
              nativeFetch(`${routes.cart}?sections=cart-drawer`, { headers: { Accept: 'application/json' } })
                .then((r) => (r.ok ? r.json() : null))
                .then((s) => { if (s && s['cart-drawer']) drawer.refresh(s['cart-drawer']); })
                .catch(() => {})
                .finally(() => drawer.open(this.submitButton));
            } else {
              drawer.open(this.submitButton);
            }
          } else if (document.getElementById('main-cart')) {
            document.querySelector('cart-items')?.refreshCartPage();
          } else if (window.MBTheme?.settings?.cartType === 'page') {
            window.location.href = routes.cart;
          }
        })
        .catch((error) => {
          const message = friendlyCartError(error);
          this.announce(message, 'error');
          document.dispatchEvent(new CustomEvent('cart:error', { detail: { message } }));
        })
        .finally(() => this.setLoading(false));
    }

    setLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', loading);
      if (loading) {
        this.submitButton.setAttribute('aria-busy', 'true');
      } else {
        this.submitButton.removeAttribute('aria-busy');
      }
    }

    announce(message, tone) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('form-status--error', tone === 'error');
      this.status.classList.toggle('form-status--success', tone === 'success');
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- <cart-drawer> ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      /* Delegated so listeners survive refresh() innerHTML swaps without
         re-binding (re-binding accumulated duplicates on the overlay button). */
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
      lockScroll();
      const panel = this.querySelector('.drawer__panel');
      if (panel) trapFocus(panel, this.querySelector('[data-drawer-close]'));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      unlockScroll();
      releaseFocus();
      if (this.opener && document.contains(this.opener)) this.opener.focus();
      this.setAttribute('inert', ''); // pair with open()'s removeAttribute — belt over the visibility:hidden braces
    }

    refresh(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer .drawer__panel');
      const panel = this.querySelector('.drawer__panel');
      if (fresh && panel) {
        /* remember what had focus so quantity/remove interactions don't get
           yanked to the close button on every refresh */
        const active = document.activeElement;
        let focusSelector = null;
        if (panel.contains(active)) {
          const line = active.closest('[data-line-key]');
          const lineKey = line?.dataset.lineKey;
          if (lineKey && active.name) {
            focusSelector = `[data-line-key="${CSS.escape(lineKey)}"] [name="${CSS.escape(active.name)}"]`;
          } else if (lineKey && active.matches('[data-quantity-input]')) {
            focusSelector = `[data-line-key="${CSS.escape(lineKey)}"] [data-quantity-input]`;
          }
        }
        panel.innerHTML = fresh.innerHTML;
        if (this.classList.contains('is-open')) {
          const restoreTo = focusSelector ? panel.querySelector(focusSelector) : null;
          trapFocus(panel, restoreTo || this.querySelector('[data-drawer-close]'), { moveFocus: !!restoreTo || !panel.contains(document.activeElement) });
        }
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* Cart icon click → open drawer (when enabled) */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-drawer-toggle]');
    if (!trigger) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || window.MBTheme?.settings?.cartType !== 'drawer') return; // navigate to /cart
    if (window.location.pathname === routes.cart) return;
    e.preventDefault();
    drawer.open(trigger);
  });

  /* ---------- <cart-items> (drawer + page quantity/remove) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-quantity-input]');
        if (!input) return;
        if (input.value === '' || Number.isNaN(Number(input.value))) {
          input.value = input.defaultValue; // don't treat a cleared field as "remove"
          return;
        }
        this.updateQuantity(input.dataset.key, Number(input.value), input);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.updateQuantity(remove.dataset.key, 0, remove);
        }
      });
    }

    updateQuantity(key, quantity, sourceEl) {
      if (!key) return;
      this.classList.add('is-busy');
      this.setAttribute('aria-busy', 'true');
      cart
        .change(key, quantity)
        .then(async (result) => {
          await this.refreshCartPage();
          this.announce(result.item_count);
        })
        .catch((error) => {
          this.announce(null, friendlyCartError(error));
          if (sourceEl && sourceEl.tagName === 'INPUT') sourceEl.value = sourceEl.defaultValue;
        })
        .finally(() => {
          this.classList.remove('is-busy');
          this.removeAttribute('aria-busy');
        });
    }

    /* On the cart page, swap the whole main-cart section via the Section
       Rendering API so totals, discounts and the empty state stay in sync. */
    async refreshCartPage() {
      /* Version the refresh: a keyboard user can queue a second quantity change
         while the first section fetch is in flight; without the token the older
         response could win and render stale totals (replaceWith on the detached
         loser node is a silent no-op). */
      const token = (this.refreshToken = (this.refreshToken || 0) + 1);
      const pageRegion = document.getElementById('main-cart');
      if (!pageRegion) return;
      const sectionEl = pageRegion.closest('.shopify-section');
      if (!sectionEl) return;
      try {
        const sectionId = sectionEl.id.replace('shopify-section-', '');
        const response = await fetch(`${routes.cart}?section_id=${encodeURIComponent(sectionId)}`, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(String(response.status));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.getElementById('main-cart');
        if (!fresh) throw new Error('missing section');
        if (token !== this.refreshToken) return; // superseded by a newer refresh
        const live = document.getElementById('main-cart');
        if (live) live.replaceWith(fresh);
      } catch {
        if (token === this.refreshToken) window.location.reload();
      }
    }

    announce(count, errorMessage) {
      /* After refreshCartPage() this element may be the detached old tree —
         announce into the live document, not the orphan. */
      const region = (this.isConnected && this.querySelector('[data-cart-status]')) || document.querySelector('[data-cart-status]');
      if (!region) return;
      if (errorMessage) {
        region.textContent = errorMessage;
      } else if (count !== null && count !== undefined) {
        const template = window.MBTheme?.strings?.cartUpdated || 'Cart updated. [count] items.';
        region.textContent = template.replace('[count]', count);
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------- <modal-dialog> / <modal-opener> ---------- */
  class ModalDialog extends HTMLElement {
    connectedCallback() {
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.querySelectorAll('[data-modal-close]').forEach((el) => el.addEventListener('click', () => this.hide()));
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.hide();
      });
    }
    show(opener) {
      if (this.hasAttribute('open')) return;
      this.opener = opener;
      this.setAttribute('open', '');
      lockScroll();
      // focus a real control, not the full-screen .modal__overlay backdrop
      // (also a [data-modal-close]) — landing there shows no focus ring and
      // Enter would immediately close the just-opened modal
      trapFocus(this, this.querySelector('.modal__close') || this.querySelector('input, button:not(.modal__overlay)'));
    }
    hide() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      unlockScroll();
      releaseFocus();
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    }
  }
  customElements.define('modal-dialog', ModalDialog);

  class ModalOpener extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button, [role="button"]');
      if (!button) return;
      button.addEventListener('click', () => {
        const modal = document.querySelector(this.dataset.modal);
        if (modal) modal.show(button);
      });
    }
  }
  customElements.define('modal-opener', ModalOpener);

  /* ---------- <details-disclosure> (desktop nav dropdowns) ---------- */
  /* Shared pointer tracker: scrolling shifts the header under a stationary
     cursor (announcement bar scrolls away, header shrinks on stick), which
     fires synthetic mouseleave events. Menus must only close when the pointer
     has GENUINELY left them once the scroll settles. */
  const lastPointer = { x: -1, y: -1 };
  document.addEventListener('mousemove', (e) => { lastPointer.x = e.clientX; lastPointer.y = e.clientY; }, { passive: true });

  class DetailsDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;
      this.summary.setAttribute('role', 'button');
      this.details.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
        if (this.details.open) this.closeSiblings();
      });
      this.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.details.open = false;
        });
      });
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' && this.details.open) {
          this.details.open = false;
          this.summary.focus();
        }
      });
      this.addEventListener('mouseleave', () => {
        if (this.dataset.hover !== 'true') return;
        if (this.scrolling) return; // scroll-induced exit — the settle check decides
        this.scheduleClose(220); // grace period: micro-jitter and gap-crossing don't kill the menu
      });
      this.addEventListener('mouseenter', () => {
        if (this.dataset.hover !== 'true') return;
        clearTimeout(this.closeTimer);
        this.details.open = true;
      });
      this.onWinScroll = () => {
        if (this.dataset.hover !== 'true' || !this.details.open) return;
        this.scrolling = true;
        clearTimeout(this.closeTimer);
        clearTimeout(this.scrollSettle);
        this.scrollSettle = setTimeout(() => {
          this.scrolling = false;
          // keyboard/touch users have no pointer — pointerInside() is always
          // false for them, so don't scroll-close a menu they're navigating
          const noPointer = lastPointer.x < 0 && lastPointer.y < 0;
          const focusInside = this.contains(document.activeElement);
          if (!noPointer && !focusInside && !this.pointerInside()) this.details.open = false;
        }, 180);
      };
      window.addEventListener('scroll', this.onWinScroll, { passive: true });
    }
    disconnectedCallback() {
      if (this.onWinScroll) window.removeEventListener('scroll', this.onWinScroll);
      clearTimeout(this.closeTimer);
      clearTimeout(this.scrollSettle);
    }
    scheduleClose(delay) {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.pointerInside()) this.details.open = false;
      }, delay);
    }
    pointerInside() {
      const panel = this.querySelector('.dropdown__panel, .mega__panel');
      const rects = [this.getBoundingClientRect()];
      if (panel) rects.push(panel.getBoundingClientRect());
      return rects.some((r) => lastPointer.x >= r.left && lastPointer.x <= r.right && lastPointer.y >= r.top && lastPointer.y <= r.bottom);
    }
    closeSiblings() {
      document.querySelectorAll('details-disclosure details[open]').forEach((other) => {
        if (!this.contains(other)) other.open = false;
      });
    }
  }
  customElements.define('details-disclosure', DetailsDisclosure);

  document.addEventListener('click', (e) => {
    const openMenu = document.querySelector('details-disclosure details[open]');
    if (openMenu && !openMenu.closest('details-disclosure').contains(e.target)) openMenu.open = false;
  });

  /* ---------- <details-modal> (header search overlay) ---------- */
  class DetailsModal extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;
      this.summary.setAttribute('role', 'button');
      this.summary.addEventListener('click', (e) => {
        e.preventDefault();
        this.details.open ? this.close() : this.open();
      });
      this.querySelectorAll('[data-search-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' && this.details.open) this.close();
      });
    }
    open() {
      if (this.details.open) return;
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      lockScroll();
      trapFocus(this.details, this.querySelector('input[type="search"]'));
    }
    close() {
      if (!this.details.open) return;
      this.details.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
      this.querySelector('predictive-search')?.close?.(); // clear stale results so reopening doesn't flash the last query
      unlockScroll();
      releaseFocus();
      this.summary.focus();
    }
  }
  customElements.define('details-modal', DetailsModal);

  /* ---------- <sticky-header> ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-wrapper') || this;
      if (this.wrapper.classList.contains('header-wrapper--static')) return; // sticky disabled in settings
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
    onScroll() {
      this.wrapper.classList.toggle('is-stuck', window.scrollY > 40);
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- <menu-drawer> (mobile navigation) ---------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.toggleButton = document.querySelector('[data-menu-drawer-toggle]');
      if (this.toggleButton) this.toggleButton.addEventListener('click', () => (this.classList.contains('is-open') ? this.close() : this.open()));
      this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    open() {
      if (this.classList.contains('is-open')) return;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.toggleButton?.setAttribute('aria-expanded', 'true');
      lockScroll();
      trapFocus(this.querySelector('.drawer__panel'), this.querySelector('[data-drawer-close]'));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.toggleButton?.setAttribute('aria-expanded', 'false');
      unlockScroll();
      releaseFocus();
      this.toggleButton?.focus();
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ---------- <slider-component> (scroll-snap carousel) ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-slider]');
      /* controls may live in the section header, outside <slider-component> —
         scope to the section; templates have at most one slider per section */
      const controlScope = this.closest('.section') || this;
      this.prevButton = controlScope.querySelector('[data-slider-prev]');
      this.nextButton = controlScope.querySelector('[data-slider-next]');
      this.counter = controlScope.querySelector('[data-slider-counter]');
      if (!this.slider) return;
      this.prevButton?.addEventListener('click', () => this.scrollBySlide(-1));
      this.nextButton?.addEventListener('click', () => this.scrollBySlide(1));
      this.slider.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      this.onResize = debounce(() => this.update(), 120);
      window.addEventListener('resize', this.onResize);
      this.update();
    }
    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }
    slideWidth() {
      const slide = this.slider.querySelector(':scope > *');
      if (!slide) return this.slider.clientWidth;
      const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }
    scrollBySlide(direction) {
      this.slider.scrollBy({ left: this.slideWidth() * direction, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.slider.scrollWidth - this.slider.clientWidth - 2;
      /* aria-disabled instead of disabled: hard-disabling the focused arrow at
         the end of the track drops keyboard focus to <body> */
      if (this.prevButton) this.prevButton.setAttribute('aria-disabled', String(this.slider.scrollLeft <= 2));
      if (this.nextButton) this.nextButton.setAttribute('aria-disabled', String(this.slider.scrollLeft >= max));
      if (this.counter) {
        const slideW = this.slideWidth() || 1;
        const perView = Math.max(1, Math.round(this.slider.clientWidth / slideW));
        const totalSlides = Math.max(1, Math.round(this.slider.scrollWidth / slideW));
        // reachable scroll positions, not raw slide count — otherwise the last
        // position reads e.g. "5 / 8" (stuck) when cards 5-8 are all on screen
        const pages = Math.max(1, totalSlides - perView + 1);
        let page = Math.round(this.slider.scrollLeft / slideW) + 1;
        if (this.slider.scrollLeft >= max) page = pages; // snap to last at the end
        this.counter.textContent = `${Math.min(page, pages)} / ${pages}`;
      }
      const hasOverflow = this.slider.scrollWidth > this.slider.clientWidth + 4;
      this.classList.toggle('slider--no-controls', !hasOverflow);
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- <deferred-media> (click-to-load video/model) ---------- */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-deferred-trigger]')?.addEventListener('click', () => this.loadContent());
    }
    loadContent() {
      if (this.getAttribute('loaded')) return;
      const template = this.querySelector('template');
      if (!template || !template.content.firstElementChild) return;
      const content = template.content.firstElementChild.cloneNode(true);
      this.appendChild(content);
      this.setAttribute('loaded', 'true');
      const trigger = this.querySelector('[data-deferred-trigger]');
      if (trigger) trigger.hidden = true; // poster is replaced by the media — drop it from view and tab order
      const focusable = content.matches(FOCUSABLE) ? content : content.querySelector(FOCUSABLE);
      focusable?.focus();
    }
  }
  customElements.define('deferred-media', DeferredMedia);

  /* ---------- scroll reveal ---------- */
  function initReveal() {
    if (!window.MBTheme?.settings?.animations) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    targets.forEach((el) => {
      el.classList.add('reveal');
      io.observe(el);
    });
  }

  /* ---------- external links in new tab (ported behavior) ---------- */
  function initExternalLinks() {
    if (!window.MBTheme?.settings?.externalLinksNewTab) return;
    document.querySelectorAll('a[href^="http"]').forEach((a) => {
      if (a.host !== window.location.host) {
        a.target = '_blank';
        if (!/\bnoopener\b/.test(a.rel)) a.rel = a.rel ? `${a.rel} noopener` : 'noopener'; // idempotent on section:load re-run
      }
    });
  }

  /* Merchant-content tables scroll horizontally on small screens (.rte table
     overflow-x) — a scrollable region must be keyboard-reachable. Idempotent. */
  function initScrollableTables() {
    document.querySelectorAll('.rte table').forEach((table) => {
      if (table.scrollWidth > table.clientWidth + 4) {
        if (!table.hasAttribute('tabindex')) {
          table.tabIndex = 0;
          table.setAttribute('role', 'region');
          table.setAttribute('aria-label', window.MBTheme?.strings?.tableRegion || 'Table, scroll horizontally');
        }
      } else if (table.getAttribute('role') === 'region') {
        table.removeAttribute('tabindex');
        table.removeAttribute('role');
        table.removeAttribute('aria-label');
      }
    });
  }

  /* Footer menus ship expanded (desktop shows them all); on phones they're
     accordions — start them collapsed or the footer is an endless scroll. */
  function collapseFooterAccordions() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    document.querySelectorAll('.footer__block--accordion .footer__details[open]').forEach((d) => {
      d.open = false;
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', collapseFooterAccordions);
  else collapseFooterAccordions();
  // also collapse when crossing into the mobile breakpoint (e.g. landscape→portrait
  // rotation), where the accordion CSS kicks in but load-time collapse didn't run
  window.matchMedia('(max-width: 749px)').addEventListener('change', (e) => {
    if (e.matches) collapseFooterAccordions();
  });

  /* Fast Bundle app: hide the entire bundle widget when any bundled item is
     sold out — the app itself only greys out the Add button. CSS :has covers
     the sold-out-tag case instantly; this also catches the "Sold out" button
     text and the app's async / variant-change re-renders. */
  function refreshFastBundles() {
    const panel = document.querySelector('[data-soldout-panel]');
    document.querySelectorAll('[id^="rbr-bundle-"]').forEach((bundle) => {
      // any Fast Bundle widget type: per-item sold-out tag, checkbox popup
      // sold-out class, the "Sold out" button, or "main product is sold out"
      const soldOut =
        !!bundle.querySelector('[class*="soldout" i], [class*="soldOut"]') ||
        /\bsold\s*out\b/i.test(bundle.textContent || '');
      bundle.style.display = soldOut ? 'none' : '';
      // keep the notify panel above a visible bundle (the app renders async)
      if (!soldOut && panel && !panel.hidden && bundle.parentElement && bundle.previousElementSibling !== panel) {
        bundle.parentElement.insertBefore(panel, bundle);
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshFastBundles);
  else refreshFastBundles();
  // the widget hydrates after load and re-renders on variant change
  const bundleObserver = new MutationObserver(debounce(refreshFastBundles, 120));
  bundleObserver.observe(document.body, { childList: true, subtree: true });

  /* Back-in-stock: submit straight to Klaviyo's client API (no Shopify form,
     so no /challenge captcha redirect). Klaviyo sends the restock email when
     the variant is available again. */
  function klaviyoCompanyId() {
    const s = document.querySelector('script[src*="klaviyo.js"]');
    const m = s && s.src.match(/company_id=([A-Za-z0-9]+)/);
    return m ? m[1] : null;
  }
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-bis-klaviyo]');
    if (!form) return;
    e.preventDefault();
    const strings = window.MBTheme?.strings || {};
    const wrap = form.closest('[data-bis-form]');
    const emailInput = form.querySelector('[data-bis-email]');
    const email = (emailInput?.value || '').trim();
    const variant = wrap?.dataset.bisVariant;
    const company = klaviyoCompanyId();
    const errEl = form.querySelector('[data-bis-error]');
    const btn = form.querySelector('[data-bis-submit]');
    const showErr = (msg) => { if (errEl) { errEl.textContent = msg; errEl.hidden = false; } };
    if (errEl) errEl.hidden = true;
    if (!email || (emailInput && !emailInput.checkValidity())) {
      return showErr(strings.bisInvalidEmail || 'Please enter a valid email address.');
    }
    if (!company || !variant) {
      return showErr(strings.bisError || 'Sorry, something went wrong. Please try again.');
    }
    if (btn) btn.disabled = true;
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email } } } },
        relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant}` } } },
      },
    };
    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${company}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(payload),
    })
      .then((r) => {
        if (r.ok || r.status === 202) {
          form.hidden = true;
          const done = wrap.querySelector('[data-bis-done]');
          if (done) done.hidden = false;
        } else {
          showErr(strings.bisError || 'Sorry, something went wrong. Please try again.');
          if (btn) btn.disabled = false;
        }
      })
      .catch(() => {
        showErr(strings.bisError || 'Sorry, something went wrong. Please try again.');
        if (btn) btn.disabled = false;
      });
  });

  /* Shopify's draft-preview toolbar (iframe #PBarNextFrame) floats over the
     bottom of the viewport and swallows clicks on the footer legal links.
     Pad the page so nothing sits underneath it; no-op on a published theme.
     The bar injects well after load, so poke a few times. */
  function padForPreviewBar() {
    const bar = document.getElementById('PBarNextFrame');
    if (!bar) return;
    // the iframe is often sized far taller than the visible strip — clamp it
    const h = Math.min(Math.ceil(bar.getBoundingClientRect().height) || 56, 72);
    // pad inside the footer, not the body — body padding shows as a bare
    // strip under the footer while footer padding blends into its background
    const target = document.querySelector('.footer') || document.body;
    target.style.paddingBottom = `${h + 8}px`;
  }
  [1000, 3000].forEach((ms) => setTimeout(padForPreviewBar, ms));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initReveal();
      initExternalLinks();
      initScrollableTables();
    });
  } else {
    initReveal();
    initExternalLinks();
    initScrollableTables();
  }
  window.addEventListener('resize', debounce(initScrollableTables, 250));

  /* Theme editor: sections re-render in place — re-observe reveals and
     re-apply external-link targets for the new nodes (both are idempotent). */
  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initExternalLinks();
    initScrollableTables();
  });
})();
